import React, { useEffect, useState, useCallback, useRef } from "react";
import { EmptyState } from "../../components/ui";
import { Icon } from "../shared/components";
import {
  ensureArray, formatMoney, getInitials,
  normalizeBrazilPhone, maskBrazilPhoneInput,
} from "../shared/utils";

// ── CNPJ helpers ──────────────────────────────────────────────────────────────

function cleanCnpj(v) { return String(v || "").replace(/\D/g, ""); }

function formatCnpj(v) {
  const d = cleanCnpj(v).slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0,2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`;
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12,14)}`;
}

async function fetchCnpjData(cnpj) {
  const res = await fetch(`https://publica.cnpj.ws/cnpj/${cleanCnpj(cnpj)}`);
  if (!res.ok) throw new Error("CNPJ não encontrado");
  return res.json();
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleDateString("pt-BR");
}

function fmtDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

const PLAN_META = {
  starter:    { label: "Starter",    color: "#6b7280", bg: "#f3f4f6" },
  growth:     { label: "Growth",     color: "#0e7163", bg: "#e8f5f3" },
  enterprise: { label: "Enterprise", color: "#7c3aed", bg: "#faf5ff" },
};

const BILLING_META = {
  trial:   { label: "Trial",     color: "#d97706", bg: "#fffbeb" },
  active:  { label: "Em dia",    color: "#16a34a", bg: "#f0fdf4" },
  late:    { label: "Em atraso", color: "#dc2626", bg: "#fef2f2" },
  blocked: { label: "Bloqueado", color: "#6b7280", bg: "#f3f4f6" },
};

const STATUS_META = {
  active:     { label: "Ativa",      color: "#16a34a", bg: "#f0fdf4" },
  onboarding: { label: "Onboarding", color: "#2563eb", bg: "#eff6ff" },
  suspended:  { label: "Suspensa",   color: "#d97706", bg: "#fffbeb" },
  blocked:    { label: "Bloqueada",  color: "#dc2626", bg: "#fef2f2" },
  deleted:    { label: "Removida",   color: "#6b7280", bg: "#f3f4f6" },
};

const ROLE_LABELS = { master: "Master", admin: "Admin", supervisor: "Supervisor", atendente: "Atendente" };

function PlanBadge({ plan }) {
  const m = PLAN_META[plan] || { label: plan || "—", color: "#6b7280", bg: "#f3f4f6" };
  return <span className="masterBadge" style={{ color: m.color, background: m.bg }}>{m.label}</span>;
}

function BillingBadge({ status }) {
  const m = BILLING_META[status] || { label: status || "—", color: "#6b7280", bg: "#f3f4f6" };
  return <span className="masterBadge" style={{ color: m.color, background: m.bg }}>{m.label}</span>;
}

function StatusBadge({ status }) {
  const m = STATUS_META[status] || { label: status || "—", color: "#6b7280", bg: "#f3f4f6" };
  return <span className="masterBadge" style={{ color: m.color, background: m.bg }}>{m.label}</span>;
}

// ── KPI Card ──────────────────────────────────────────────────────────────────

function MasterKpi({ label, value, sub, tone }) {
  return (
    <div className={`masterKpiCard tone-${tone || "neutral"}`}>
      <span className="masterKpiLabel">{label}</span>
      <strong className="masterKpiValue">{value}</strong>
      {sub && <small className="masterKpiSub">{sub}</small>}
    </div>
  );
}

// ── Company Drawer ────────────────────────────────────────────────────────────

function CompanyDrawer({ companyId, api, onClose, onSaved }) {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("info");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState(null);
  const [newUserForm, setNewUserForm] = useState({ name: "", email: "", password: "123456", role: "atendente" });
  const [addingUser, setAddingUser] = useState(false);
  const [userActionLoading, setUserActionLoading] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/saas/companies/${companyId}`);
      setCompany(res.data);
      setForm({
        name: res.data.name || "",
        email: res.data.email || "",
        phone: res.data.phone || "",
        cnpj: res.data.cnpj || "",
        plan_name: res.data.plan_name || "growth",
        billing_status: res.data.billing_status || "trial",
        status: res.data.status || "active",
        max_users: res.data.max_users || 5,
        max_conversations: res.data.max_conversations || 1000,
      });
    } catch {
      setNotice("Erro ao carregar empresa.");
    } finally {
      setLoading(false);
    }
  }, [companyId, api]);

  useEffect(() => { load(); }, [load]);

  async function saveInfo(e) {
    e.preventDefault();
    setSaving(true);
    setNotice("");
    try {
      await api.patch(`/admin/saas/companies/${companyId}`, { ...form, phone: normalizeBrazilPhone(form.phone) });
      setNotice("✓ Dados salvos com sucesso.");
      await load();
      onSaved?.();
    } catch (err) {
      setNotice(err.response?.data?.error || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleUser(userId, active) {
    setUserActionLoading(userId);
    try {
      await api.patch(`/admin/saas/users/${userId}`, { active });
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Erro ao alterar usuário.");
    } finally {
      setUserActionLoading(null);
    }
  }

  async function resetPassword(userId) {
    const pwd = window.prompt("Nova senha para o usuário (mínimo 6 caracteres):", "123456");
    if (!pwd) return;
    setUserActionLoading(userId);
    try {
      await api.patch(`/admin/saas/users/${userId}`, { password: pwd });
      setNotice("✓ Senha redefinida com sucesso.");
    } catch (err) {
      setNotice(err.response?.data?.error || "Erro ao redefinir senha.");
    } finally {
      setUserActionLoading(null);
    }
  }

  async function createUser(e) {
    e.preventDefault();
    setAddingUser(true);
    setNotice("");
    try {
      await api.post(`/admin/saas/companies/${companyId}/users`, newUserForm);
      setNewUserForm({ name: "", email: "", password: "123456", role: "atendente" });
      setNotice("✓ Usuário criado com sucesso.");
      await load();
    } catch (err) {
      setNotice(err.response?.data?.error || "Erro ao criar usuário.");
    } finally {
      setAddingUser(false);
    }
  }

  async function deleteCompany() {
    try {
      await api.delete(`/admin/saas/companies/${companyId}`);
      onSaved?.();
      onClose();
    } catch (err) {
      setNotice(err.response?.data?.error || "Erro ao remover empresa.");
    }
  }

  if (loading) {
    return (
      <div className="masterDrawerBackdrop" onClick={onClose}>
        <div className="masterDrawer" onClick={(e) => e.stopPropagation()}>
          <div className="masterDrawerLoading">Carregando...</div>
        </div>
      </div>
    );
  }

  const users = ensureArray(company?.users);

  return (
    <div className="masterDrawerBackdrop" onClick={onClose}>
      <div className="masterDrawer" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="masterDrawerHeader">
          <div className="masterDrawerTitle">
            <div className="masterCmpAvatar">{getInitials(company?.name)}</div>
            <div>
              <strong>{company?.name}</strong>
              <span>{company?.email || "Sem e-mail"}</span>
            </div>
          </div>
          <div className="masterDrawerBadges">
            <PlanBadge plan={company?.plan_name} />
            <BillingBadge status={company?.billing_status} />
            <StatusBadge status={company?.status} />
          </div>
          <button className="masterDrawerClose" onClick={onClose}>✕</button>
        </div>

        {/* KPI strip */}
        <div className="masterDrawerKpis">
          {[
            ["Usuários", `${company?.users_active}/${company?.users_total}`, "contact"],
            ["Conversas abertas", company?.conversations_open, "chat"],
            ["Mensagens", company?.messages_total, "activity"],
            ["Leads ganhos", company?.leads_won, "deal"],
            ["Receita", formatMoney(company?.won_value || 0), "report"],
          ].map(([l, v, ic]) => (
            <div className="masterDrawerKpiItem" key={l}>
              <span>{l}</span><strong>{v}</strong>
            </div>
          ))}
        </div>

        {notice && <div className={`masterDrawerNotice ${notice.startsWith("✓") ? "ok" : "err"}`}>{notice}</div>}

        {/* Tabs */}
        <div className="masterDrawerTabs">
          {[["info", "Dados"], ["users", `Usuários (${users.length})`], ["danger", "Avançado"]].map(([id, label]) => (
            <button key={id} className={tab === id ? "active" : ""} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>

        <div className="masterDrawerBody">
          {/* ── INFO TAB ── */}
          {tab === "info" && form && (
            <form className="masterDrawerForm" onSubmit={saveInfo}>
              <div className="masterFormRow">
                <div>
                  <label>Nome da empresa</label>
                  <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                </div>
                <div>
                  <label>Email</label>
                  <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                </div>
              </div>
              <div className="masterFormRow">
                <div>
                  <label>Telefone</label>
                  <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: maskBrazilPhoneInput(e.target.value) }))} />
                </div>
                <div>
                  <label>CNPJ</label>
                  <input value={form.cnpj} onChange={(e) => setForm((f) => ({ ...f, cnpj: e.target.value }))} />
                </div>
              </div>
              <div className="masterFormRow three">
                <div>
                  <label>Plano</label>
                  <select value={form.plan_name} onChange={(e) => setForm((f) => ({ ...f, plan_name: e.target.value }))}>
                    <option value="starter">Starter</option>
                    <option value="growth">Growth</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
                <div>
                  <label>Faturamento</label>
                  <select value={form.billing_status} onChange={(e) => setForm((f) => ({ ...f, billing_status: e.target.value }))}>
                    <option value="trial">Trial</option>
                    <option value="active">Em dia</option>
                    <option value="late">Em atraso</option>
                    <option value="blocked">Bloqueado</option>
                  </select>
                </div>
                <div>
                  <label>Status</label>
                  <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                    <option value="active">Ativa</option>
                    <option value="onboarding">Onboarding</option>
                    <option value="suspended">Suspensa</option>
                    <option value="blocked">Bloqueada</option>
                  </select>
                </div>
              </div>
              <div className="masterFormRow">
                <div>
                  <label>Limite de usuários</label>
                  <input type="number" min={1} value={form.max_users} onChange={(e) => setForm((f) => ({ ...f, max_users: Number(e.target.value) }))} />
                </div>
                <div>
                  <label>Limite de conversas</label>
                  <input type="number" min={50} value={form.max_conversations} onChange={(e) => setForm((f) => ({ ...f, max_conversations: Number(e.target.value) }))} />
                </div>
              </div>
              <div className="masterDrawerActions">
                <button type="submit" disabled={saving}>{saving ? "Salvando…" : "Salvar alterações"}</button>
              </div>
            </form>
          )}

          {/* ── USERS TAB ── */}
          {tab === "users" && (
            <div className="masterUserSection">
              <div className="masterUserList">
                {users.map((u) => (
                  <div className="masterUserRow" key={u.id}>
                    <div className="masterUserAvatar" style={{ opacity: u.active ? 1 : 0.45 }}>{getInitials(u.name)}</div>
                    <div className="masterUserInfo">
                      <strong style={{ opacity: u.active ? 1 : 0.5 }}>{u.name}</strong>
                      <span>{u.email}</span>
                    </div>
                    <span className="masterUserRole">{ROLE_LABELS[u.role] || u.role}</span>
                    <div className="masterUserActions">
                      <button
                        className={u.active ? "masterUserBtn warn" : "masterUserBtn ok"}
                        disabled={userActionLoading === u.id}
                        onClick={() => toggleUser(u.id, !u.active)}
                        title={u.active ? "Desativar" : "Ativar"}
                      >
                        {userActionLoading === u.id ? "…" : u.active ? "Desativar" : "Ativar"}
                      </button>
                      <button
                        className="masterUserBtn"
                        disabled={userActionLoading === u.id}
                        onClick={() => resetPassword(u.id)}
                        title="Redefinir senha"
                      >
                        🔑 Senha
                      </button>
                    </div>
                  </div>
                ))}
                {users.length === 0 && <p className="masterEmptyText">Nenhum usuário cadastrado.</p>}
              </div>

              {/* Add user form */}
              <div className="masterAddUserBox">
                <div className="masterAddUserToggle">
                  <strong>+ Adicionar usuário</strong>
                  <small>Limite: {company?.users_total}/{company?.max_users}</small>
                </div>
                <form className="masterDrawerForm compact" onSubmit={createUser}>
                  <div className="masterFormRow">
                    <div><label>Nome</label><input value={newUserForm.name} onChange={(e) => setNewUserForm((f) => ({ ...f, name: e.target.value }))} placeholder="João Silva" /></div>
                    <div><label>Email</label><input value={newUserForm.email} onChange={(e) => setNewUserForm((f) => ({ ...f, email: e.target.value }))} placeholder="joao@empresa.com" /></div>
                  </div>
                  <div className="masterFormRow">
                    <div><label>Senha inicial</label><input value={newUserForm.password} onChange={(e) => setNewUserForm((f) => ({ ...f, password: e.target.value }))} /></div>
                    <div>
                      <label>Perfil</label>
                      <select value={newUserForm.role} onChange={(e) => setNewUserForm((f) => ({ ...f, role: e.target.value }))}>
                        <option value="atendente">Atendente</option>
                        <option value="supervisor">Supervisor</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  </div>
                  <button type="submit" disabled={addingUser}>{addingUser ? "Criando…" : "Criar usuário"}</button>
                </form>
              </div>
            </div>
          )}

          {/* ── DANGER TAB ── */}
          {tab === "danger" && (
            <div className="masterDangerZone">
              <div className="masterDangerCard">
                <div>
                  <strong>Suspender empresa</strong>
                  <p>Bloqueia o acesso de todos os usuários sem remover os dados.</p>
                </div>
                <button
                  className="masterDangerBtn warn"
                  onClick={() => {
                    setForm((f) => ({ ...f, status: "suspended" }));
                    setTab("info");
                  }}
                >
                  Suspender
                </button>
              </div>
              <div className="masterDangerCard">
                <div>
                  <strong>Remover empresa</strong>
                  <p>Marca como deletada e desativa todos os usuários. Ação irreversível.</p>
                </div>
                {!confirmDelete ? (
                  <button className="masterDangerBtn danger" onClick={() => setConfirmDelete(true)}>Remover</button>
                ) : (
                  <div className="masterDangerConfirm">
                    <span>Tem certeza? Esta ação não pode ser desfeita.</span>
                    <button className="masterDangerBtn danger" onClick={deleteCompany}>Confirmar remoção</button>
                    <button className="masterDangerBtn" onClick={() => setConfirmDelete(false)}>Cancelar</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ── FinanceiroTab ─────────────────────────────────────────────────────────────

function FinanceiroTab({ companies }) {
  const PLAN_PRICES = { starter: 49.9, growth: 99.9, enterprise: 199.9 };
  const PLAN_COLORS = { starter: "#6b7280", growth: "#0e7163", enterprise: "#7c3aed", premium: "#7c3aed" };

  const active = companies.filter((c) => c.billing_status === "active" || c.billing_status === "trial");
  const mrr = companies
    .filter((c) => c.billing_status === "active")
    .reduce((sum, c) => sum + (PLAN_PRICES[c.plan_name] || 0), 0);
  const inadimplentes = companies.filter((c) => c.billing_status === "late").length;
  const aVencer = companies.filter((c) => {
    if (!c.next_billing_date) return false;
    const days = (new Date(c.next_billing_date) - Date.now()) / 86400000;
    return days >= 0 && days <= 30;
  }).length;
  const suspensos = companies.filter((c) => c.status === "suspended" || c.status === "blocked").length;

  const byPlan = companies.reduce((acc, c) => {
    const k = c.plan_name || "—";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  // Histórico simplificado: agrupa por mês de criação (últimos 6)
  const historyMap = new Map();
  for (let i = 0; i < 6; i++) {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    const key = `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
    historyMap.set(key, { count: 0, value: 0 });
  }
  companies.forEach((c) => {
    if (!c.created_at) return;
    const d = new Date(c.created_at);
    const key = `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
    if (historyMap.has(key)) {
      const e = historyMap.get(key);
      e.count++;
      e.value += PLAN_PRICES[c.plan_name] || 0;
    }
  });
  const history = [...historyMap.entries()].reverse();
  const maxCount = Math.max(1, ...history.map(([, e]) => e.count));

  return (
    <div className="masterTabContent">
      <div className="masterKpiStrip">
        <MasterKpi label="MRR ESTIMADO" value={formatMoney(mrr)} sub="soma planos ativos" tone="blue" />
        <MasterKpi label="INADIMPLENTES" value={inadimplentes} sub="plano expirado ativo" tone="red" />
        <MasterKpi label="A VENCER (30D)" value={aVencer} sub="empresas em risco" tone="amber" />
        <MasterKpi label="SUSPENSOS" value={suspensos} sub="bloqueados manualmente" tone="neutral" />
      </div>

      <div className="masterPanel">
        <h4>Distribuição por plano</h4>
        {Object.keys(byPlan).length === 0 ? (
          <p className="masterEmpty">Sem empresas cadastradas.</p>
        ) : (
          <div className="masterPlanList">
            {Object.entries(byPlan).sort((a, b) => b[1] - a[1]).map(([plan, count]) => (
              <div className="masterPlanRow" key={plan}>
                <span className="masterPlanDot" style={{ background: PLAN_COLORS[plan] || "#6b7280" }} />
                <strong>{(PLAN_META[plan]?.label) || plan}</strong>
                <span>{count} empresa{count !== 1 ? "s" : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="masterPanel">
        <h4>Histórico de cadastros (últimos 6 meses)</h4>
        <div className="masterHistory">
          {history.map(([month, data]) => (
            <div key={month} className="masterHistoryRow">
              <span className="masterHistoryMonth">{month}</span>
              <div className="masterHistoryBar">
                <div style={{ width: `${(data.count / maxCount) * 100}%` }} />
              </div>
              <span className="masterHistoryCount">{data.count} empresas</span>
              <span className="masterHistoryValue">{formatMoney(data.value)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── BillingTab ────────────────────────────────────────────────────────────────

function BillingTab({ api, companies }) {
  const [tolerance, setTolerance] = useState({ days_after_due: 5, days_warning_before: 7 });
  const [savingTol, setSavingTol] = useState(false);

  const counts = {
    em_dia: companies.filter((c) => c.billing_status === "active").length,
    a_vencer: companies.filter((c) => c.billing_status === "trial").length,
    vencidos: companies.filter((c) => c.billing_status === "late").length,
    bloqueados: companies.filter((c) => c.billing_status === "blocked").length,
    suspensos: companies.filter((c) => c.status === "suspended").length,
  };

  function saveTolerance() {
    setSavingTol(true);
    setTimeout(() => {
      try { localStorage.setItem("sz_billing_tolerance", JSON.stringify(tolerance)); } catch {}
      setSavingTol(false);
    }, 300);
  }

  useEffect(() => {
    try {
      const saved = localStorage.getItem("sz_billing_tolerance");
      if (saved) setTolerance(JSON.parse(saved));
    } catch {}
  }, []);

  return (
    <div className="masterTabContent">
      <div className="masterStatusGrid">
        <div className="masterStatusCard tone-green">
          <strong>{counts.em_dia}</strong>
          <span>EM DIA</span>
        </div>
        <div className="masterStatusCard tone-amber">
          <strong>{counts.a_vencer}</strong>
          <span>A VENCER</span>
        </div>
        <div className="masterStatusCard tone-orange">
          <strong>{counts.vencidos}</strong>
          <span>VENCIDOS</span>
        </div>
        <div className="masterStatusCard tone-red">
          <strong>{counts.bloqueados}</strong>
          <span>BLOQUEADOS</span>
        </div>
        <div className="masterStatusCard tone-neutral">
          <strong>{counts.suspensos}</strong>
          <span>SUSPENSOS</span>
        </div>
      </div>

      <div className="masterPanel">
        <h4>CONFIGURAÇÕES DE TOLERÂNCIA</h4>
        <div className="masterFormRow">
          <div>
            <label>Dias de tolerância após vencimento</label>
            <input
              type="number"
              min={0}
              value={tolerance.days_after_due}
              onChange={(e) => setTolerance((t) => ({ ...t, days_after_due: Number(e.target.value) }))}
            />
            <small className="masterFieldHint">Dias após o vencimento antes de bloquear recursos críticos</small>
          </div>
          <div>
            <label>Dias de aviso antes do vencimento</label>
            <input
              type="number"
              min={0}
              value={tolerance.days_warning_before}
              onChange={(e) => setTolerance((t) => ({ ...t, days_warning_before: Number(e.target.value) }))}
            />
            <small className="masterFieldHint">Exibe banner "vence em X dias" com essa antecedência</small>
          </div>
        </div>
        <button className="masterSecondaryBtn" onClick={saveTolerance} disabled={savingTol}>
          {savingTol ? "Salvando..." : "Salvar configurações"}
        </button>
      </div>

      <div className="masterPanel">
        <h4>STATUS POR EMPRESA</h4>
        <div className="masterCompanyTable simple">
          <div className="masterCompanyTableHead simple">
            <span>EMPRESA</span>
            <span>PLANO</span>
            <span>MENSALIDADE</span>
            <span>VENCIMENTO</span>
            <span>STATUS</span>
            <span>SITUAÇÃO</span>
          </div>
          {companies.map((c) => {
            const PLAN_PRICES = { starter: 49.9, growth: 99.9, enterprise: 199.9 };
            return (
              <div className="masterCompanyRow simple" key={c.id}>
                <strong>{c.name}</strong>
                <span>{(PLAN_META[c.plan_name]?.label) || c.plan_name || "—"}</span>
                <span>{formatMoney(PLAN_PRICES[c.plan_name] || 0)}</span>
                <span>{fmtDate(c.next_billing_date)}</span>
                <BillingBadge status={c.billing_status} />
                <span className={`masterSituacao ${c.billing_status}`}>
                  {c.billing_status === "active" ? "✓ Regular" : c.billing_status === "late" ? "⚠ Atrasado" : c.billing_status === "blocked" ? "✕ Bloqueado" : "—"}
                </span>
              </div>
            );
          })}
          {companies.length === 0 && <div className="masterEmpty">Nenhuma empresa.</div>}
        </div>
      </div>
    </div>
  );
}

// ── FinanceiroAvancadoTab ─────────────────────────────────────────────────────

function FinanceiroAvancadoTab({ companies }) {
  const PLAN_PRICES = { starter: 49.9, growth: 99.9, enterprise: 199.9 };
  const mrr = companies.filter((c) => c.billing_status === "active").reduce((s, c) => s + (PLAN_PRICES[c.plan_name] || 0), 0);
  const recPrevista = mrr;
  const recRecebida = 0;
  const recAtrasada = companies.filter((c) => c.billing_status === "late").reduce((s, c) => s + (PLAN_PRICES[c.plan_name] || 0), 0);
  const lateCount = companies.filter((c) => c.billing_status === "late").length;
  const inadPct = mrr > 0 ? Math.round((recAtrasada / mrr) * 1000) / 10 : 0;
  const monthLabel = new Date().toLocaleDateString("pt-BR", { year: "numeric", month: "2-digit" }).split("/").reverse().join("-");

  return (
    <div className="masterTabContent">
      <div className="masterFinHeader">
        <strong className="masterFinHeading">DASHBOARD FINANCEIRO — {monthLabel}</strong>
        <button className="masterSecondaryBtn" disabled title="Em breve">▶ Rodar automação agora</button>
      </div>

      <div className="masterKpiStrip wide">
        <MasterKpi label="MRR (RECURRING MENSAL)" value={formatMoney(mrr)} sub={`${companies.length} assinaturas`} tone="green" />
        <MasterKpi label="RECEITA PREVISTA (MÊS)" value={formatMoney(recPrevista)} tone="blue" />
        <MasterKpi label="RECEITA RECEBIDA (MÊS)" value={formatMoney(recRecebida)} tone="green" />
        <MasterKpi label="RECEITA ATRASADA" value={formatMoney(recAtrasada)} sub={`${lateCount} cobrança(s)`} tone="red" />
        <MasterKpi label="INADIMPLÊNCIA" value={`${inadPct}%`} tone="orange" />
        <MasterKpi label="CRESCIMENTO MOM" value="0.0%" tone="green" />
        <MasterKpi label="CHURN (MÊS)" value="0.0%" tone="neutral" />
        <MasterKpi label="TOTAL DE EMPRESAS" value={companies.length} tone="blue" />
      </div>

      <div className="masterPanel">
        <h4>RECEITA ÚLTIMOS 12 MESES</h4>
        <div className="masterChartPlaceholder">
          <em>Em construção — endpoint de série temporal pendente</em>
        </div>
      </div>

      <div className="masterPanel">
        <h4>EVENTOS DA AUTOMAÇÃO (ÚLTIMOS 30)</h4>
        <p className="masterEmpty">Nenhum evento registrado. Habilite os webhooks Asaas em Integrações.</p>
      </div>
    </div>
  );
}

// ── AuditoriaTab ──────────────────────────────────────────────────────────────

function AuditoriaTab({ api }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const res = await api.get("/admin/saas/activity?limit=50&offset=0");
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
    } catch {}
    setLoading(false);
  }

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="masterTabContent">
      <div className="masterPanelHeader">
        <span className="masterPanelHeaderInfo">{total} evento{total !== 1 ? "s" : ""} de auditoria master</span>
        <button className="masterSecondaryBtn" onClick={load}>Atualizar</button>
      </div>

      {loading ? (
        <div className="masterEmpty">Carregando…</div>
      ) : rows.length === 0 ? (
        <div className="masterEmpty">Nenhum evento registrado.</div>
      ) : (
        <div className="masterAuditTable">
          <div className="masterAuditHead">
            <span>EVENTO</span>
            <span>ATOR</span>
            <span>DATA/HORA</span>
          </div>
          {rows.map((r) => (
            <div className="masterAuditRow" key={r.id}>
              <strong>{r.action?.replace(/_/g, "_") || "ação"}</strong>
              <div className="masterAuditActor">
                <span>{r.user_name || "Sistema"}</span>
                {r.ip && <small>{r.ip}</small>}
              </div>
              <time>{fmtDateTime(r.created_at)}</time>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── SegurancaTab ──────────────────────────────────────────────────────────────

function SegurancaTab({ api, companies }) {
  const [resetForm, setResetForm] = useState({ company_id: "", new_admin_password: "" });
  const [masterForm, setMasterForm] = useState({ current: "", next: "" });
  const [notice, setNotice] = useState("");

  async function saveResetAdmin() {
    if (!resetForm.company_id || !resetForm.new_admin_password) {
      setNotice("Selecione a empresa e informe a nova senha.");
      return;
    }
    if (resetForm.new_admin_password.length < 6) {
      setNotice("A senha deve ter pelo menos 6 caracteres.");
      return;
    }
    try {
      const usersRes = await api.get(`/admin/saas/companies/${resetForm.company_id}`);
      const adminUser = (usersRes.data?.users || []).find((u) => u.role === "admin");
      if (!adminUser) { setNotice("Empresa sem admin cadastrado."); return; }
      await api.patch(`/admin/saas/users/${adminUser.id}`, { password: resetForm.new_admin_password });
      setNotice("✓ Senha do admin atualizada com sucesso.");
      setResetForm({ company_id: "", new_admin_password: "" });
    } catch (err) {
      setNotice(err.response?.data?.error || "Erro ao salvar.");
    }
  }

  async function saveMasterPassword() {
    if (!masterForm.current || !masterForm.next) {
      setNotice("Informe a senha atual e a nova senha.");
      return;
    }
    if (masterForm.next.length < 6) {
      setNotice("A nova senha deve ter pelo menos 6 caracteres.");
      return;
    }
    try {
      await api.post("/admin/saas/master-password", {
        current_password: masterForm.current,
        new_password: masterForm.next,
      });
      setNotice("✓ Senha master alterada com sucesso.");
      setMasterForm({ current: "", next: "" });
    } catch (err) {
      setNotice(err.response?.data?.error || "Erro ao alterar senha.");
    }
  }

  return (
    <div className="masterTabContent">
      {notice && <div className={`masterNotice ${notice.startsWith("✓") ? "ok" : "err"}`}>{notice}<button onClick={() => setNotice("")}>✕</button></div>}

      <div className="masterSecurityGrid">
        <div className="masterPanel">
          <h4>Resetar senha do admin da empresa</h4>
          <p className="masterEmpty">Selecione a empresa para definir uma nova senha para o usuário admin (quem faz login no sistema).</p>

          <label>EMPRESA</label>
          <select value={resetForm.company_id} onChange={(e) => setResetForm((f) => ({ ...f, company_id: e.target.value }))}>
            <option value="">Selecione...</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <label>NOVA SENHA DO ADMIN</label>
          <div className="masterInlineSave">
            <input
              type="password"
              placeholder="Mínimo 6 caracteres"
              value={resetForm.new_admin_password}
              onChange={(e) => setResetForm((f) => ({ ...f, new_admin_password: e.target.value }))}
            />
            <button className="masterSecondaryBtn" onClick={saveResetAdmin}>Salvar</button>
          </div>
        </div>

        <div className="masterPanel">
          <h4>Senha master</h4>
          <p className="masterEmpty">Altere a senha da conta master. Requer confirmação da senha atual.</p>

          <label>SENHA ATUAL</label>
          <input type="password" value={masterForm.current} onChange={(e) => setMasterForm((f) => ({ ...f, current: e.target.value }))} />

          <label>NOVA SENHA</label>
          <input type="password" value={masterForm.next} onChange={(e) => setMasterForm((f) => ({ ...f, next: e.target.value }))} />

          <button className="masterPrimaryBtn" onClick={saveMasterPassword} style={{ marginTop: 14 }}>Alterar senha master</button>
        </div>
      </div>
    </div>
  );
}

// ── Main MasterAdminPage ──────────────────────────────────────────────────────

function MasterAdminPage({ api, onLogout }) {
  const [activeTab, setActiveTab] = useState("companies");
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [filterPlan, setFilterPlan] = useState("");
  const [filterBilling, setFilterBilling] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [notice, setNotice] = useState("");
  const [createForm, setCreateForm] = useState({
    company_name: "", cnpj: "", phone: "", email: "",
    plan_name: "growth", billing_status: "trial", status: "active",
    max_users: 5, max_conversations: 1000,
    admin_name: "", admin_email: "", admin_password: "123456",
  });
  const [creating, setCreating] = useState(false);
  const [cnpjStatus, setCnpjStatus] = useState("idle"); // idle | loading | found | error
  const cnpjTokenRef = useRef(0);

  async function handleCnpjChange(raw) {
    const digits = cleanCnpj(raw);
    const masked = formatCnpj(digits);
    setCreateForm((f) => ({ ...f, cnpj: masked }));
    if (digits.length !== 14) { setCnpjStatus("idle"); return; }
    const token = ++cnpjTokenRef.current;
    setCnpjStatus("loading");
    try {
      const data = await fetchCnpjData(digits);
      if (token !== cnpjTokenRef.current) return;
      const phone = [data.ddd_telefone_1, data.ddd_telefone_2].find(Boolean) || "";
      setCreateForm((f) => ({
        ...f,
        cnpj: masked,
        company_name: f.company_name || data.razao_social || f.company_name,
        phone: f.phone || maskBrazilPhoneInput(phone.replace(/\D/g, "")),
        email: f.email || (data.email || "").toLowerCase(),
      }));
      setCnpjStatus("found");
    } catch {
      if (token !== cnpjTokenRef.current) return;
      setCnpjStatus("error");
    }
  }

  async function loadAll() {
    try {
      const comps = await api.get("/admin/saas/companies");
      setCompanies(comps.data || []);
    } catch (err) {
      setNotice(err.response?.data?.error || "Erro ao carregar empresas.");
    }
  }

  useEffect(() => { loadAll(); }, []);

  async function createCompany(e) {
    e.preventDefault();
    setCreating(true);
    setNotice("");
    try {
      await api.post("/admin/saas/companies", { ...createForm, phone: normalizeBrazilPhone(createForm.phone) });
      setNotice("✓ Empresa criada com sucesso!");
      setCreateForm({
        company_name: "", cnpj: "", phone: "", email: "",
        plan_name: "growth", billing_status: "trial", status: "active",
        max_users: 5, max_conversations: 1000,
        admin_name: "", admin_email: "", admin_password: "123456",
      });
      setActiveTab("companies");
      await loadAll();
    } catch (err) {
      setNotice(err.response?.data?.error || "Erro ao criar empresa.");
    } finally {
      setCreating(false);
    }
  }

  const filteredCompanies = companies.filter((c) => {
    const q = search.toLowerCase();
    const matchSearch = !q || c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q);
    const matchPlan = !filterPlan || c.plan_name === filterPlan;
    const matchBilling = !filterBilling || c.billing_status === filterBilling;
    return matchSearch && matchPlan && matchBilling;
  });

  const TABS = [
    { id: "companies", label: `Empresas (${companies.length})` },
    { id: "financeiro",   label: "Financeiro" },
    { id: "billing",      label: "Billing" },
    { id: "fin_avancado", label: "Financeiro Avançado" },
    { id: "auditoria",    label: "Auditoria" },
    { id: "seguranca",    label: "Segurança" },
  ];

  // KPIs do topo (sempre visíveis em todas as tabs)
  const PLAN_PRICES = { starter: 49.9, growth: 99.9, enterprise: 199.9 };
  const ativos = companies.filter((c) => c.billing_status === "active" || c.billing_status === "trial").length;
  const aVencer = companies.filter((c) => {
    if (!c.next_billing_date) return false;
    const days = (new Date(c.next_billing_date) - Date.now()) / 86400000;
    return days >= 0 && days <= 30;
  }).length;
  const inadimplentes = companies.filter((c) => c.billing_status === "late").length;
  const mrr = companies.filter((c) => c.billing_status === "active").reduce((sum, c) => sum + (PLAN_PRICES[c.plan_name] || 0), 0);
  const totalUsers = companies.reduce((sum, c) => sum + (Number(c.users_active || 0)), 0);

  return (
    <div className="masterAdminPage">
      {/* KPI strip — visível em todas as tabs */}
      <div className="masterKpiStrip top">
        <MasterKpi label="TOTAL DE EMPRESAS" value={companies.length} sub={`${totalUsers} usuários`} tone="neutral" />
        <MasterKpi label="ATIVOS" value={ativos} tone="green" />
        <MasterKpi label="A VENCER" value={aVencer} tone="amber" />
        <MasterKpi label="INADIMPLENTES" value={inadimplentes} tone="red" />
        <MasterKpi label="MRR" value={formatMoney(mrr)} sub="soma planos ativos" tone="blue" />
      </div>

      {notice && (
        <div className={`masterNotice ${notice.startsWith("✓") ? "ok" : "err"}`}>
          {notice}
          <button onClick={() => setNotice("")}>✕</button>
        </div>
      )}

      {/* Tabs + botões à direita */}
      <div className="masterTabsRow">
        <div className="masterTabs">
          {TABS.map((t) => (
            <button key={t.id} className={activeTab === t.id ? "active" : ""} onClick={() => setActiveTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {onLogout && (
            <button
              className="masterSecondaryBtn"
              onClick={onLogout}
              title="Encerrar sessão"
            >
              ← Sair
            </button>
          )}
          <button className="masterPrimaryBtn" onClick={() => setActiveTab("create")}>
            + Nova empresa
          </button>
        </div>
      </div>

      {/* Tab content */}
      {activeTab === "financeiro"   && <FinanceiroTab companies={companies} />}
      {activeTab === "billing"      && <BillingTab api={api} companies={companies} />}
      {activeTab === "fin_avancado" && <FinanceiroAvancadoTab companies={companies} />}
      {activeTab === "auditoria"    && <AuditoriaTab api={api} />}
      {activeTab === "seguranca"    && <SegurancaTab api={api} companies={companies} />}

      {/* ── COMPANIES TAB ── */}
      {activeTab === "companies" && (
        <div className="masterTabContent">
          <div className="masterCompanyBar">
            <input
              className="masterSearchInput"
              placeholder="Buscar empresa ou e-mail…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select value={filterPlan} onChange={(e) => setFilterPlan(e.target.value)} className="masterFilterSelect">
              <option value="">Todos os planos</option>
              <option value="starter">Starter</option>
              <option value="growth">Growth</option>
              <option value="enterprise">Enterprise</option>
            </select>
            <select value={filterBilling} onChange={(e) => setFilterBilling(e.target.value)} className="masterFilterSelect">
              <option value="">Todos os billing</option>
              <option value="trial">Trial</option>
              <option value="active">Em dia</option>
              <option value="late">Em atraso</option>
              <option value="blocked">Bloqueado</option>
            </select>
            <span className="masterCountLabel">{filteredCompanies.length} empresa{filteredCompanies.length !== 1 ? "s" : ""}</span>
          </div>

          {filteredCompanies.length === 0 ? (
            <EmptyState illustration="search" title="Nenhuma empresa encontrada" text="Tente ajustar os filtros ou crie uma nova empresa." />
          ) : (
            <div className="masterCompanyTable">
              <div className="masterCompanyTableHead">
                <span>Empresa</span>
                <span>Plano</span>
                <span>Billing</span>
                <span>Status</span>
                <span>Usuários</span>
                <span>Conversas</span>
                <span>Mensagens</span>
                <span></span>
              </div>
              {filteredCompanies.map((c) => (
                <div className="masterCompanyRow" key={c.id} onClick={() => setSelectedCompanyId(c.id)}>
                  <div className="masterCmpNameCell">
                    <div className="masterCmpAvatar sm">{getInitials(c.name)}</div>
                    <div>
                      <strong>{c.name}</strong>
                      <span>{c.email || "Sem e-mail"}</span>
                    </div>
                  </div>
                  <PlanBadge plan={c.plan_name} />
                  <BillingBadge status={c.billing_status} />
                  <StatusBadge status={c.status} />
                  <span className="masterCmpStat">{c.users_active}/{c.max_users}</span>
                  <span className="masterCmpStat">{c.conversations_open}</span>
                  <span className="masterCmpStat">{c.messages_total || 0}</span>
                  <span className="masterCmpArrow">→</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── CREATE TAB ── */}
      {activeTab === "create" && (
        <div className="masterTabContent">
          <div className="masterCreateGrid">
            <div className="masterCreatePanel">
              <h3>Criar nova empresa</h3>
              <p>Configure a conta com administrador, plano e limites operacionais. Um e-mail com credenciais será disponibilizado ao finalizar.</p>

              <form className="masterCreateForm" onSubmit={createCompany}>
                <fieldset>
                  <legend>Empresa</legend>
                  <div className="masterFormRow">
                    <div>
                      <label>CNPJ
                        {cnpjStatus === "loading" && <span style={{ marginLeft: 6, fontSize: 11, color: "var(--ink-3)" }}>⏳ consultando…</span>}
                        {cnpjStatus === "found"   && <span style={{ marginLeft: 6, fontSize: 11, color: "#16a34a" }}>✅ preenchido automaticamente</span>}
                        {cnpjStatus === "error"   && <span style={{ marginLeft: 6, fontSize: 11, color: "#dc2626" }}>❌ não encontrado</span>}
                      </label>
                      <input
                        value={createForm.cnpj}
                        onChange={(e) => handleCnpjChange(e.target.value)}
                        placeholder="00.000.000/0000-00"
                        maxLength={18}
                      />
                    </div>
                    <div><label>Nome da empresa *</label><input value={createForm.company_name} onChange={(e) => setCreateForm((f) => ({ ...f, company_name: e.target.value }))} placeholder="Ex: Empresa XYZ Ltda" required /></div>
                  </div>
                  <div className="masterFormRow">
                    <div><label>Telefone comercial</label><input value={createForm.phone} onChange={(e) => setCreateForm((f) => ({ ...f, phone: maskBrazilPhoneInput(e.target.value) }))} placeholder="+55 (83) 99999-9999" /></div>
                    <div><label>Email da empresa</label><input value={createForm.email} onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))} placeholder="contato@empresa.com" /></div>
                  </div>
                </fieldset>

                <fieldset>
                  <legend>Administrador</legend>
                  <div className="masterFormRow">
                    <div><label>Nome *</label><input value={createForm.admin_name} onChange={(e) => setCreateForm((f) => ({ ...f, admin_name: e.target.value }))} placeholder="Nome do admin" required /></div>
                    <div><label>Email *</label><input value={createForm.admin_email} onChange={(e) => setCreateForm((f) => ({ ...f, admin_email: e.target.value }))} placeholder="admin@empresa.com" required /></div>
                  </div>
                  <div className="masterFormRow">
                    <div><label>Senha inicial *</label><input value={createForm.admin_password} onChange={(e) => setCreateForm((f) => ({ ...f, admin_password: e.target.value }))} required /></div>
                  </div>
                </fieldset>

                <fieldset>
                  <legend>Plano e limites</legend>
                  <div className="masterFormRow three">
                    <div>
                      <label>Plano</label>
                      <select value={createForm.plan_name} onChange={(e) => setCreateForm((f) => ({ ...f, plan_name: e.target.value }))}>
                        <option value="starter">Starter</option>
                        <option value="growth">Growth</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>
                    <div>
                      <label>Billing</label>
                      <select value={createForm.billing_status} onChange={(e) => setCreateForm((f) => ({ ...f, billing_status: e.target.value }))}>
                        <option value="trial">Trial</option>
                        <option value="active">Em dia</option>
                        <option value="late">Em atraso</option>
                        <option value="blocked">Bloqueado</option>
                      </select>
                    </div>
                    <div>
                      <label>Status inicial</label>
                      <select value={createForm.status} onChange={(e) => setCreateForm((f) => ({ ...f, status: e.target.value }))}>
                        <option value="active">Ativa</option>
                        <option value="onboarding">Onboarding</option>
                        <option value="trial">Trial</option>
                      </select>
                    </div>
                  </div>
                  <div className="masterFormRow">
                    <div><label>Limite de usuários</label><input type="number" min={1} value={createForm.max_users} onChange={(e) => setCreateForm((f) => ({ ...f, max_users: Number(e.target.value) }))} /></div>
                    <div><label>Limite de conversas</label><input type="number" min={50} value={createForm.max_conversations} onChange={(e) => setCreateForm((f) => ({ ...f, max_conversations: Number(e.target.value) }))} /></div>
                  </div>
                </fieldset>

                <button type="submit" disabled={creating} className="masterPrimaryBtn">
                  {creating ? "Criando empresa…" : "Criar empresa no SaaS"}
                </button>
              </form>
            </div>

            <div className="masterCreateInfo">
              <h4>O que acontece ao criar?</h4>
              <div className="masterCreateStep">
                <span className="masterCreateStepNum">1</span>
                <div><strong>Empresa criada</strong><p>Registro isolado no banco multi-tenant com os dados informados.</p></div>
              </div>
              <div className="masterCreateStep">
                <span className="masterCreateStepNum">2</span>
                <div><strong>Admin configurado</strong><p>Usuário com role "admin" e as credenciais definidas aqui.</p></div>
              </div>
              <div className="masterCreateStep">
                <span className="masterCreateStepNum">3</span>
                <div><strong>Funil padrão</strong><p>6 etapas criadas automaticamente: Novo Lead → Fechado.</p></div>
              </div>
              <div className="masterCreateStep">
                <span className="masterCreateStepNum">4</span>
                <div><strong>Pronto para usar</strong><p>O cliente conecta o WhatsApp e começa a operar imediatamente.</p></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Company Drawer */}
      {selectedCompanyId && (
        <CompanyDrawer
          companyId={selectedCompanyId}
          api={api}
          onClose={() => setSelectedCompanyId(null)}
          onSaved={() => { setSelectedCompanyId(null); loadAll(); }}
        />
      )}
    </div>
  );
}

export default MasterAdminPage;
